import { BusinessHoursDisplay } from '@/components/shared/BusinessHoursDisplay';
import type { Amenity } from '@/config/objects/amenities';
import type { Hotel } from '@/config/objects/hotels';
import { formatDate } from '@startupquickstart/frontend';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  EmptyTableState,
  Loading,
  MapPreview,
  PageContainer,
  RichTextContent,
  StatusPill,
  useApiClient,
  useApp,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Building2,
  ExternalLink,
  FileText,
  Globe,
  Layers,
  Mail,
  Pencil,
  Phone,
  Sparkles,
  Trash2,
  Users,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

export function AmenityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { isInternal } = useRequireOrganization();
  const { config } = useApp();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: Amenity }>({
    queryKey: ['Amenity', id],
    queryFn: () => apiClient.get(`/amenities/${id}`),
    enabled: !!id,
  });

  const amenity = data?.data;

  const { data: hotelData } = useQuery<{ success: boolean; data: Hotel }>({
    queryKey: ['Hotel', amenity?.hotelId],
    queryFn: () => apiClient.get(`/hotels/${amenity!.hotelId}`),
    enabled: !!amenity?.hotelId,
  });

  const hotel = hotelData?.data;

  const deleteMutation = useMutation({
    mutationFn: async (amenityId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/amenities/${amenityId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Amenity'] });
        navigate('/amenities');
      } else {
        throw new Error(response.error || 'Failed to delete amenity');
      }
    },
  });

  const handleDelete = () => {
    if (!amenity) return;
    toast.promise(deleteMutation.mutateAsync(amenity.id), {
      loading: 'Deleting amenity...',
      success: 'Amenity deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete amenity',
    });
  };

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Amenity Not Found</h2>
          <p className="text-muted-foreground">The requested amenity could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Hotels', href: '/hotels' },
    ...(amenity?.hotel
      ? [{ label: amenity.hotel.name, href: `/hotels/${amenity.hotel.id}/amenities` }]
      : []),
    { label: amenity?.name || 'Amenity', current: true },
  ];

  const imageUrl = amenity?.featuredImage?.url ?? null;

  const hasContactInfo = amenity?.phoneNumber || amenity?.email || amenity?.website;
  const hasHours = amenity?.hours && Object.keys(amenity.hours).length > 0;

  return (
    <PageContainer padding="none" className="flex flex-col min-h-full">
      <DetailPageHeader
        actions={
          amenity && (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/amenities/${id}/edit`)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              {isInternal && (
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loading />
        </div>
      ) : (
        <DetailPageHero
          isLoading={false}
          category={{
            label: amenity?.amenityType ? (
              <>
                Amenity
                <span className="text-white/40">&middot;</span>
                {amenity.amenityType.name}
              </>
            ) : (
              'Amenity'
            ),
            icon: Sparkles,
          }}
          title={amenity?.name || 'Amenity'}
          image={{
            url: imageUrl,
            alt: amenity?.name || 'Amenity',
            fallbackIcon: Sparkles,
          }}
          meta={
            <>
              {amenity?.hotel && (
                <Link
                  to={`/hotels/${amenity.hotel.id}/overview`}
                  className="flex items-center gap-1 hover:text-white/90 transition-colors"
                >
                  <Building2 className="h-3.5 w-3.5" />
                  {amenity.hotel.name}
                </Link>
              )}
              {amenity?.floor && (
                <span className="flex items-center gap-1">
                  <Layers className="h-3.5 w-3.5" />
                  Floor {amenity.floor}
                </span>
              )}
              {amenity?.capacity != null && (
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  Capacity: {amenity.capacity}
                </span>
              )}
            </>
          }
          badges={amenity?.status && <StatusPill status={amenity.status} />}
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Left column */}
              <div className="lg:col-span-3 space-y-6">
                {/* About card */}
                <div className="rounded-xl border bg-card shadow-sm">
                  <div className="px-6 py-4 border-b">
                    <h2 className="text-lg font-medium tracking-tight text-zinc-900">About</h2>
                    <p className="text-sm text-muted-foreground">Amenity description and details</p>
                  </div>
                  <div className="p-6">
                    {amenity?.description ? (
                      <RichTextContent content={amenity.description} />
                    ) : (
                      <EmptyTableState
                        icon={<FileText className="h-6 w-6" />}
                        title="No description"
                        description="Edit to add a description"
                      />
                    )}
                  </div>
                  <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
                    <span>Created: {formatDate(amenity?.createdAt)}</span>
                    <span>Updated: {formatDate(amenity?.updatedAt)}</span>
                  </div>
                </div>

                {/* Contact Information card */}
                <div className="rounded-xl border bg-card shadow-sm">
                  <div className="px-6 py-4 border-b">
                    <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                      Contact Information
                    </h2>
                    <p className="text-sm text-muted-foreground">How guests can reach this amenity</p>
                  </div>
                  <div className="p-6">
                    {hasContactInfo ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="min-w-0">
                          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5 shrink-0" />
                            Phone
                          </div>
                          <div className="text-sm">
                            {amenity?.phoneNumber || (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 shrink-0" />
                            Email
                          </div>
                          <div className="text-sm truncate">
                            {amenity?.email ? (
                              <a
                                href={`mailto:${amenity.email}`}
                                className="text-primary hover:underline"
                                title={amenity.email}
                              >
                                {amenity.email}
                              </a>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                            <Globe className="h-3.5 w-3.5 shrink-0" />
                            Website
                          </div>
                          <div className="text-sm truncate">
                            {amenity?.website ? (
                              <a
                                href={amenity.website.startsWith('http') ? amenity.website : `https://${amenity.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline inline-flex items-center gap-1"
                                title={amenity.website.replace(/^https?:\/\//, '')}
                              >
                                <span className="truncate">{amenity.website.replace(/^https?:\/\//, '')}</span>
                                <ExternalLink className="h-3 w-3 shrink-0" />
                              </a>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No contact information provided</p>
                    )}
                  </div>
                </div>

                {/* Hours card */}
                {hasHours && (
                  <div className="rounded-xl border bg-card shadow-sm">
                    <div className="px-6 py-4 border-b">
                      <h2 className="text-lg font-medium tracking-tight text-zinc-900">Hours</h2>
                      <p className="text-sm text-muted-foreground">Operating hours and schedule</p>
                    </div>
                    <div className="p-6">
                      <BusinessHoursDisplay value={amenity?.hours} />
                    </div>
                  </div>
                )}
              </div>

              {/* Right column */}
              <div className="lg:col-span-2">
                <div className="rounded-xl border bg-card shadow-sm">
                  <div className="px-6 py-4 border-b">
                    <h2 className="text-lg font-medium tracking-tight text-zinc-900">Location</h2>
                    <p className="text-sm text-muted-foreground">Address and placement details</p>
                  </div>
                  <div className="p-6 space-y-0">
                    <MapPreview
                      latitude={hotel?.latitude}
                      longitude={hotel?.longitude}
                      googleMapsApiKey={config?.googleMapsApiKey}
                      alt={`Map of ${hotel?.name || amenity?.name}`}
                      className="mb-4"
                    />

                    <div className="divide-y">
                      {amenity?.floor && (
                        <div className="py-3 first:pt-0">
                          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                            Floor
                          </div>
                          <div className="text-sm">{amenity.floor}</div>
                        </div>
                      )}
                      {amenity?.capacity != null && (
                        <div className="py-3 first:pt-0">
                          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                            Capacity
                          </div>
                          <div className="text-sm">{amenity.capacity}</div>
                        </div>
                      )}
                      {(() => {
                        const h = hotel;
                        if (!h) return null;
                        const addressLines = [
                          h.streetAddress,
                          h.streetAddress_2,
                          [h.city, h.state].filter(Boolean).join(', '),
                          h.postalCode,
                          h.country,
                        ].filter(Boolean);
                        if (addressLines.length === 0) return null;
                        return (
                          <div className="py-3">
                            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                              Street Address
                            </div>
                            <div className="text-sm whitespace-pre-line">
                              {addressLines.join('\n')}
                            </div>
                          </div>
                        );
                      })()}
                      {hotel && (
                        <div className="py-3">
                          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                            Hotel
                          </div>
                          <div className="text-sm">
                            <Link
                              to={`/hotels/${hotel.id}/overview`}
                              className="text-primary hover:underline"
                            >
                              {hotel.name}
                            </Link>
                          </div>
                        </div>
                      )}
                      {(hotel?.latitude != null && hotel?.longitude != null) && (
                        <div className="py-3">
                          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                            Coordinates
                          </div>
                          <div className="text-sm">
                            {hotel.latitude}, {hotel.longitude}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </DetailPageHero>
      )}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Amenity"
        description={`Are you sure you want to delete "${amenity?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
